import React, { useCallback, useMemo, useState, useEffect } from 'react'
import apiClient from '../utils/api/apiClient'
import { APIEndpoints } from '../utils/api/apiConfig'
import { emptyGuid, isEmptyGuid } from '../utils/emptyGuid'
import { useWithdrawalsState } from './WithdrawalsProvider'
import { cloneDeep } from 'lodash';
import { setValueByKey } from '../utils/utils'
import { setPointerErrorsFromApiResponse } from "../utils/api/apiUtils"
import { newGuid } from '../utils/objectutils'
import { useTranslation } from "react-i18next";

const PickingControlDataContext = React.createContext({})
export function usePickingControlDataState() {
    return React.useContext(PickingControlDataContext)
}
const headerDataTemplate = {
    id: emptyGuid,
    displayName: '',
    recipient: {
        id: emptyGuid
    },
    office: {
        id: emptyGuid
    },
    costCenter: {
        id: emptyGuid
    },
    sourceStorehouse: {
        id: emptyGuid
    },
    targetStorehouse: {
        id: emptyGuid
    }
}

const PickingControlDataProvider = ({ children }) => {
    const [successSubmit, setSuccessSubmit] = useState(false)
    const [headerData, setHeaderData] = useState(cloneDeep(headerDataTemplate))
    const [initialHeaderData, setInitialHeaderData] = useState('')
    const [positions, setPositions] = useState([])
    const [initialPositions, setInitialPositions] = useState('')
    const [formErrors, setFormErrors] = useState({})
    const [errorsOccurredOnSubmit, setErrorsOccurredOnSubmit] = useState(false)
    const [loadedOrderData, setLoadedOrderData] = useState(null)
    const { setForceReloadWithdrawals } = useWithdrawalsState()
    const [hasBeenPickedResult, setHasBeenPickedResult] = useState({})
    const [splitPositionId, setSplitPositionId] = useState(null)
    const [lockInfo, setLockInfo] = useState(null)
    const { t } = useTranslation();

    const setHeaderDataByKey = useCallback((key, value) => {
        setHeaderData(prev => setValueByKey(key, value, prev))
    }, [])
    const setPositionsByKey = useCallback((key, value) => {
        setPositions(prev => setValueByKey(key, value, prev))
    }, [])

    const resetErrorByFormKey = useCallback(formKey => {
        setFormErrors(prev => {
            if (prev[formKey]) {
                delete prev[formKey]
            }
            return prev
        })
    }, [setFormErrors])
    const addFormErrors = useCallback((index, name) => {
        //const newFormErrors = [...formErrors]
        const newFormErrors = cloneDeep(formErrors)
        let formKey = `positions${index}${name}`
        let msg = ''
        switch (name) {
            case 'serialkey':
                msg = t('withdrawalsTable:serialkeyAlreadyExists')
                break;
            case 'rfid':
                msg = t('withdrawalsTable:scanCodeAlreadyExists')
                break;
            default:
                break
        }
        newFormErrors[formKey] = msg
        setFormErrors(newFormErrors)
    }, [formErrors, setFormErrors, t])
    const checkPosition = useCallback(id => {
        let index = positions.findIndex((item) => item.id === id)
        if (index >= 0) {
            let position = positions[index]
            if (!!position.article.useSerialKeys && !!position.serialkey) {
                //seriakey only unique within article => shippingOrderPositionId
                let foundSK = positions.find((item) => item.article.id === position.article.id && item.id !== position.id && item.serialkey === position.serialkey)
                if (foundSK) {
                    addFormErrors(index, 'serialkey')
                }
            } else if (!!position.article.useRFIDs && !!position.rfid) {
                //rfid unique over all article
                let foundRFID = positions.find((item) => item.id !== position.id && item.rfid === position.rfid)
                if (foundRFID) {
                    addFormErrors(index, 'rfid')
                }
            }
        }
    }, [positions, addFormErrors])

    const loadLockInfo = useCallback(async (id) => {
        let lockInfo = await apiClient.getJson(APIEndpoints.withdrawals(id).lockShippingOrder)
        setLockInfo(lockInfo)
        /*const newHeaderData = {
            ...headerData,
            lockInfo: lockInfo
        };
        setHeaderData(newHeaderData)
        setInitialHeaderData(JSON.stringify(newHeaderData))*/;
    }, [])

    const initLoadedOrderData = useCallback(async (loadedData) => {
        const loadedHeaderData = loadedData.header
        const loadedPositions = loadedData.positions || []

        // init headerData fields
        const newHeaderData = {
            ...loadedHeaderData,
            recipient: loadedHeaderData.recipient ? loadedHeaderData.recipient : { id: emptyGuid },
            office: loadedHeaderData.office ? loadedHeaderData.office : { id: emptyGuid },
            costCenter: loadedHeaderData.costCenter ? loadedHeaderData.costCenter : { id: emptyGuid },
            sourceStorehouse: loadedHeaderData.sourceStorehouse ? loadedHeaderData.sourceStorehouse : { id: emptyGuid },
            targetStorehouse: loadedHeaderData.targetStorehouse ? loadedHeaderData.targetStorehouse : { id: emptyGuid },
        };
        setHeaderData(newHeaderData)
        setInitialHeaderData(JSON.stringify(newHeaderData));

        setLockInfo(newHeaderData.lockInfo)
        // init positions
        const newPositions = [...loadedPositions]
        setPositions(newPositions)
        setInitialPositions(JSON.stringify(newPositions))
    }, [])
    const loadOrderData = useCallback(async (id) => {
        let loadedData = await apiClient.getJson(APIEndpoints.withdrawals(id).pickingControl)
        setLoadedOrderData(loadedData)
        initLoadedOrderData(cloneDeep(loadedData));
    }, [initLoadedOrderData])
    const headerDataHasChanged = useMemo(() => {
        if (initialHeaderData !== '') {
            const headerDataString = JSON.stringify(headerData)
            return headerDataString !== initialHeaderData
        }
    }, [headerData, initialHeaderData])
    
    const positionsHasChanged = useMemo(() => {
        if (initialPositions !== '') {
            const positionsString = JSON.stringify(positions)
            return positionsString !== initialPositions
        }
    }, [positions, initialPositions])



    const formDataHasChanged = useMemo(() => {
        return headerDataHasChanged || positionsHasChanged
    }, [headerDataHasChanged, positionsHasChanged])



    const resetFormData = useCallback(() => {
        setHeaderData(JSON.parse(initialHeaderData))
        setPositions(JSON.parse(initialPositions))
        setFormErrors({})
        setErrorsOccurredOnSubmit(false)
    }, [setFormErrors, setErrorsOccurredOnSubmit, initialHeaderData, initialPositions])

    const getPreparedPositions = useCallback(() => {
        const preparedPositions = cloneDeep(positions)
        preparedPositions.forEach(item => {
            delete item.validation
            delete item.metaProperties
        })
        return preparedPositions
    }, [positions])
    const checkStatusHasBeenPicked = () => {
        return headerData.status === 'isCompletelyShipped' || headerData.status === 'hasBeenPicked'
    }
    const checkPositionIsPicked = (pos) => {
        return pos.quantityPicked >= pos.quantityExpected
    }
    const checkPositionHasOpenQuantity = (pos) => {
        return pos.quantityExpected > pos.quantityCounted
    }
    const checkPositionQuantityCountedWasChanged = (pos) => {
        return pos.quantityPicked < pos.quantityCounted
    }
    const allPositionsHaveBeenPicked = useMemo(() => {
        return !getPreparedPositions().some((pos) => !checkPositionIsPicked(pos))
    }, [getPreparedPositions])
    const partialPickingIsEnabled = useMemo(() => {
        return getPreparedPositions().some((pos) => (!!pos.quantityCounted && checkPositionQuantityCountedWasChanged(pos) && !checkPositionIsPicked(pos)) || !!pos.isNewPosition)
    }, [getPreparedPositions])
    const hasBeenPickedIsEnabled = useMemo(() => {
        return !getPreparedPositions().some((pos) => !pos.quantityCounted || checkPositionHasOpenQuantity(pos))
    }, [getPreparedPositions])
    const submitPartialPicking = async () => {
        setFormErrors({})
        setErrorsOccurredOnSubmit(false)
        try {
            await triggerPartialPicking()
            await loadOrderData(headerData.id)
            setSuccessSubmit(true)
            setForceReloadWithdrawals(true)
        } catch (e) {
            setErrorsOccurredOnSubmit(true)
            setFormErrors(setPointerErrorsFromApiResponse(e))
            await loadLockInfo(headerData.id)
        }
    }
    const triggerPartialPicking = async () => {
        const updatedData = {
            positions: getPreparedPositions().map((pos) => {
                let preparedPos = { Id: pos.shippingOrderPositionId, QuantityCounted: pos.quantityCounted, Serialkey: pos.serialkey, Batchkey: pos.batchkey, rfid: pos.rfid, InstanceCode: pos.instanceCode, QuantityExpected: pos.quantityExpected };
                return preparedPos;
            })
        }
        await apiClient.postJson(APIEndpoints.withdrawals(headerData.id).pickingControl, updatedData)
    }
    const submitHasBeenPicked = async () => {
        setFormErrors({})
        setErrorsOccurredOnSubmit(false)
        try {
            await triggerPartialPicking()
            let resultHasBeenPicked = await apiClient.postJson(APIEndpoints.withdrawals(headerData.id).hasBeenPicked, {})
            await loadOrderData(headerData.id)
            setHasBeenPickedResult(resultHasBeenPicked)
            setSuccessSubmit(true)
            setForceReloadWithdrawals(true)
        } catch (e) {
            setErrorsOccurredOnSubmit(true)
            setFormErrors(setPointerErrorsFromApiResponse(e))
            await loadLockInfo(headerData.id)
        }
    }
    const splitPosition = useCallback(async (positionId, splitQuantity = 1, clearBatchKey = false) => {
        const foundIndex = positions.findIndex(item => item.id === positionId)
        if (foundIndex >= 0) {
            let splitPosition = positions[foundIndex]
            const newPosistions = [...positions]
            const clonedPosition = cloneDeep(splitPosition)
            clonedPosition.quantityExpected = clonedPosition.quantityExpected - splitQuantity

            if (clonedPosition.quantityExpected <= 0) {
                //quantitity is not splitable
                return
            }
            clonedPosition.id = newGuid()
            clonedPosition.serialkey = ""
            clonedPosition.rfid = ""
            clonedPosition.quantityPicked = 0
            if (clearBatchKey) {
                clonedPosition.batchkey = ""
            }
            clonedPosition.isNewPosition = true
            //clonedPosition.metaProperties = []
            //clonedPosition.validation= []
            if (clonedPosition.quantityCounted > 0) {
                clonedPosition.quantityCounted = clonedPosition.quantityCounted - splitQuantity
            }
            if (clonedPosition.article.useRFIDs || clonedPosition.article.useSerialKeys) {
                clonedPosition.quantityCounted = 0
            }
            //change quantities of old position
            newPosistions[foundIndex].quantityCounted = splitQuantity
            newPosistions[foundIndex].quantityExpected = splitQuantity
            newPosistions[foundIndex].enforceUpdate = true

            let firstPositions = newPosistions.slice(0, foundIndex + 1);
            let lastPositions = newPosistions.slice(foundIndex + 1);
            firstPositions.push(clonedPosition)

            let mergedPositions = firstPositions.concat(lastPositions);

            setPositions(mergedPositions)
        }
    }, [setPositions, positions])

    const addScannedArticle = useCallback(async (scanResponse) => {
        let { article, articleVariant } = scanResponse
        if (scanResponse.isWithSizes && isEmptyGuid(articleVariant?.id)) {
            throw new Error(t('inventory:invalidScanResult'))
        }
        if (!positions.some(pos => checkPositionHasOpenQuantity(pos))) {
            throw new Error(t('picking:allPositionsAreAlreadyScanned'))
        }
        const result = []
        for (let i = 0; i < positions.length; i++) {
            if (positions[i].article.id === article.id) {
                result.push({
                    data: positions[i],
                    index: i,
                    hasOpenQuantity: checkPositionHasOpenQuantity(positions[i]),
                    isPicked: checkPositionIsPicked(positions[i])
                })
            }
        }
        if (result?.length) {
            let foundobj = null;
            if (scanResponse.useSerialKeys && scanResponse.serialKey) {
                foundobj = result.find(item => item.data.serialkey === scanResponse.serialKey)
                if (!!foundobj?.isPicked)
                    throw new Error(t('picking:positionsIsAlreadyPicked'))
                if (!!foundobj && !foundobj.hasOpenQuantity)
                    throw new Error(t('picking:positionsIsAlreadyScanned'))
            }
            else if (scanResponse.useRFIDs && scanResponse.rfid) {
                foundobj = result.find(item => item.data.rfid === scanResponse.rfid)
                if (!!foundobj?.isPicked)
                    throw new Error(t('picking:positionsIsAlreadyPicked'))
                if (!!foundobj && !foundobj?.hasOpenQuantity)
                    throw new Error(t('picking:positionsIsAlreadyScanned'))
            }
            else if (scanResponse.useBatchKeys && scanResponse.batchKey) {
                foundobj = result.find(item => item.data.batchkey === scanResponse.batchKey && !item.isPicked && item.hasOpenQuantity)
            }
            if (!foundobj) {
                let result2 = result.filter(item => !item.isPicked && item.hasOpenQuantity)
                if (scanResponse.isWithSizes) {
                    foundobj = result2.find(item => item.data.articleVariant?.id === articleVariant?.id)
                }
                else if (result2.length) {
                    foundobj = result2[0]
                }
            }
            if (foundobj) {
                let mappedPosition = cloneDeep(foundobj.data)
                if (mappedPosition.article.useSerialKeys && scanResponse.serialKey) {
                    mappedPosition.serialkey = scanResponse.serialKey
                }
                else if (mappedPosition.article.useBatchKeys && scanResponse.batchKey) {
                    mappedPosition.batchkey = scanResponse.batchKey
                }
                if (mappedPosition.article.useSerialKeys || mappedPosition.article.useRFIDs) {
                    mappedPosition.quantityCounted = 1
                }
                else {
                    mappedPosition.quantityCounted = mappedPosition.quantityCounted + 1
                }
                const newPosistions = [...positions]
                mappedPosition.enforceUpdate = true
                newPosistions[foundobj.index] = mappedPosition
                setPositions(newPosistions)
                if ((mappedPosition.article.useRFIDs || mappedPosition.article.useSerialKeys) && mappedPosition.quantityCounted > 0 && mappedPosition.quantityExpected > 1) {
                    setSplitPositionId(mappedPosition.id)
                }
                return true
            }
        }
        throw new Error(t('picking:positionCouldNotBeAdded'))

    }, [setPositions, t, positions])

    useEffect(() => {
        if (splitPositionId) {
            splitPosition(splitPositionId)
            setSplitPositionId(null)
        }
    }, [setSplitPositionId, splitPosition, splitPositionId])

    const contextValues = {
        splitPosition, addScannedArticle,
        loadOrderData,
        submitPartialPicking, submitHasBeenPicked,
        formErrors, setFormErrors,
        resetErrorByFormKey,
        errorsOccurredOnSubmit,
        formDataHasChanged,
        resetFormData,
        setHeaderDataByKey,
        headerData,
        setPositionsByKey,
        setPositions, checkPosition,
        positions,
        setSuccessSubmit,
        successSubmit,
        loadedOrderData, lockInfo,
        hasBeenPickedResult, checkStatusHasBeenPicked,
        partialPickingIsEnabled, hasBeenPickedIsEnabled, allPositionsHaveBeenPicked
    }
    return (
        <PickingControlDataContext.Provider value={contextValues}>
            {children}
        </PickingControlDataContext.Provider>
    )
}

export default PickingControlDataProvider
